import React, { useEffect, useRef } from 'react';

import { RovoSettingsModal } from '@atlassian/rovo-settings-modal/ui/rovo-settings-modal';

/**
 * Analytics-attribution tag for opens triggered by the Confluence deep link.
 * A local string (not added to the platform `RovoSettingsModalSurface` enum,
 * which is for surfaces with an active experiment); a non-`search-*` surface
 * already defaults the discovery tab on.
 */
const CONNECTOR_DEEP_LINK_SURFACE = 'confluence-deep-link';

/**
 * Opens the modal once when `isActive` becomes true, then re-arms when it goes
 * false (e.g. after the URL is cleaned up) so a later re-appearance opens again.
 *
 * Receives the modal's stable `open` as a prop and calls it from an effect — the
 * open trigger and the ref guard live in the commit phase, never during render.
 */
const OpenOnActive = ({
	open,
	isActive,
	onHandled,
}: {
	open: (options?: { defaultTab?: string }) => void;
	isActive: boolean;
	onHandled: () => void;
}) => {
	const openedRef = useRef(false);

	useEffect(() => {
		if (isActive) {
			if (!openedRef.current) {
				openedRef.current = true;
				open({ defaultTab: 'discovery' });
				onHandled();
			}
		} else {
			openedRef.current = false;
		}
	}, [isActive, open, onHandled]);

	return null;
};

/**
 * Wraps `RovoSettingsModal` for the deep link. Lazy-loaded by
 * `ConnectorDeepLinkLoader` so the modal's weight stays out of the main chunk
 * and only loads when a deep link is actually present.
 */
export const ConnectorDeepLinkModal = ({
	cloudId,
	isActive,
	onHandled,
}: {
	cloudId: string;
	isActive: boolean;
	onHandled: () => void;
}): React.JSX.Element => (
	<RovoSettingsModal product="confluence" cloudId={cloudId} surface={CONNECTOR_DEEP_LINK_SURFACE}>
		{({ open }) => <OpenOnActive open={open} isActive={isActive} onHandled={onHandled} />}
	</RovoSettingsModal>
);
